import { useMemo } from "react";

import { AgCharts } from "ag-charts-react";
import type { AgChartOptions } from "ag-charts-community";

import { cn } from "@/lib/utils";

import { ensureAgChartsRegistered } from "./agTheme";

// Defensive: guarantee the community modules are registered even if this panel
// is the first AG import to execute.
ensureAgChartsRegistered();

/**
 * Reusable AG Charts panel — options in, chart out. Build brand-themed options
 * with {@link buildAgOptions} (or hand-roll them) and pass them here. Sized to
 * fill its container; override with `className` (e.g. `h-64`).
 *
 * `onSeriesNodeClick` wires a click on a data point (bar/line marker/pie slice)
 * to the datum behind it — the hook the Reports drill-down uses to navigate. In
 * AG Charts v14 the click listener lives on each *series* (not the chart), so we
 * inject `listeners.seriesNodeClick` into every series.
 */
export function AgChartPanel({
  options,
  className,
  onSeriesNodeClick,
}: {
  options: AgChartOptions;
  className?: string;
  onSeriesNodeClick?: (datum: Record<string, unknown>) => void;
}) {
  const opts = useMemo<AgChartOptions>(() => {
    if (!onSeriesNodeClick) return options;
    const handler = (event: { datum?: unknown }) => {
      if (event?.datum) onSeriesNodeClick(event.datum as Record<string, unknown>);
    };
    const series = (options.series ?? []).map((s) => {
      const so = s as unknown as Record<string, unknown>;
      return {
        ...so,
        // 'nearest' so a click anywhere near a datum drills in — the default
        // 'exact' needs a pixel-perfect hit on a (small) marker.
        nodeClickRange: "nearest",
        listeners: {
          ...((so.listeners as Record<string, unknown> | undefined) ?? {}),
          seriesNodeClick: handler,
        },
      };
    });
    return { ...options, series } as unknown as AgChartOptions;
  }, [options, onSeriesNodeClick]);

  return (
    <div className={cn("h-[22rem] w-full", onSeriesNodeClick && "cursor-pointer", className)}>
      <AgCharts options={opts} style={{ height: "100%", width: "100%" }} />
    </div>
  );
}
